/**
 * ConnectionQualityIndicator Component
 * 
 * Displays real-time connection quality status during video calls.
 * Shows network quality, reconnection status, and provides visual feedback.
 * 
 * Uses Daily.co's network quality events and provides reconnection handling.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useDaily, useDailyEvent } from '@daily-co/daily-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Alert,
  AlertDescription,
} from '@/components/ui/alert';
import {
  Wifi,
  WifiOff,
  Signal,
  SignalLow,
  SignalMedium,
  SignalHigh,
  AlertTriangle,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

type NetworkQuality = 'good' | 'low' | 'very-low' | 'unknown';
type ConnectionState = 'connected' | 'connecting' | 'reconnecting' | 'disconnected' | 'error';

interface ConnectionQualityIndicatorProps {
  showLabel?: boolean;
  showReconnectButton?: boolean;
  onReconnect?: () => void;
  className?: string;
}

const ConnectionQualityIndicator: React.FC<ConnectionQualityIndicatorProps> = ({
  showLabel = false,
  showReconnectButton = true,
  onReconnect,
  className = '',
}) => {
  const daily = useDaily();
  const { toast } = useToast();

  const [networkQuality, setNetworkQuality] = useState<NetworkQuality>('unknown');
  const [connectionState, setConnectionState] = useState<ConnectionState>('connecting');
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const [isManualReconnecting, setIsManualReconnecting] = useState(false);
  const [lastQualityChangeTime, setLastQualityChangeTime] = useState<Date | null>(null);

  // Handle network quality updates
  useDailyEvent(
    'network-quality-change',
    useCallback((event) => {
      if (event?.threshold) {
        const quality = event.threshold as NetworkQuality;
        setNetworkQuality(quality);
        setLastQualityChangeTime(new Date());

        // Show toast for quality changes
        if (quality === 'very-low') {
          toast({
            title: 'Poor Connection',
            description: 'Your connection quality is very low. Video may be affected.',
            variant: 'destructive',
          });
        } else if (quality === 'low') {
          toast({
            title: 'Connection Warning',
            description: 'Your connection quality is low. Consider turning off video.',
          });
        }
      }
    }, [toast])
  );

  // Handle connection state changes
  useDailyEvent(
    'meeting-state-change',
    useCallback((event) => {
      const state = event?.state;
      switch (state) {
        case 'joined-meeting':
          setConnectionState('connected');
          setReconnectAttempts(0);
          break;
        case 'joining-meeting':
          setConnectionState('connecting');
          break;
        case 'left-meeting':
          setConnectionState('disconnected');
          break;
        case 'error':
          setConnectionState('error');
          break;
      }
    }, [])
  );

  // Handle reconnection events
  useDailyEvent(
    'nonfatal-error',
    useCallback((event) => {
      if (event?.type === 'reconnection-error') {
        setConnectionState('reconnecting');
        setReconnectAttempts(prev => prev + 1);
        
        toast({
          title: 'Reconnecting',
          description: `Attempting to reconnect... (Attempt ${reconnectAttempts + 1})`,
        });
      }
    }, [reconnectAttempts, toast])
  );

  // Auto-reconnect monitoring
  useEffect(() => {
    if (connectionState === 'reconnecting' && reconnectAttempts >= 3) {
      toast({
        title: 'Connection Issues',
        description: 'Having trouble reconnecting. You may need to rejoin the call.',
        variant: 'destructive',
      });
    }
  }, [connectionState, reconnectAttempts, toast]);

  const handleManualReconnect = useCallback(async () => {
    if (!daily) return;

    try {
      setIsManualReconnecting(true);
      
      // Leave and rejoin
      await daily.leave();
      
      // Wait a moment before rejoining
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // If onReconnect is provided, call it (parent handles reconnection)
      if (onReconnect) {
        onReconnect();
      }
      
      toast({
        title: 'Reconnecting',
        description: 'Attempting to rejoin the call...',
      });
    } catch (error) {
      console.error('Manual reconnect failed:', error);
      toast({
        title: 'Reconnect Failed',
        description: 'Could not reconnect. Please refresh the page.',
        variant: 'destructive',
      });
    } finally {
      setIsManualReconnecting(false);
    }
  }, [daily, onReconnect, toast]);

  const getQualityIcon = () => {
    switch (networkQuality) {
      case 'good':
        return <SignalHigh className="w-4 h-4 text-green-500" />;
      case 'low':
        return <SignalMedium className="w-4 h-4 text-yellow-500" />;
      case 'very-low':
        return <SignalLow className="w-4 h-4 text-red-500" />;
      default:
        return <Signal className="w-4 h-4 text-slate-400" />;
    }
  };

  const getQualityLabel = () => {
    switch (networkQuality) {
      case 'good':
        return 'Good';
      case 'low':
        return 'Low';
      case 'very-low':
        return 'Poor';
      default:
        return 'Checking...';
    }
  };

  const getQualityColor = () => {
    switch (networkQuality) {
      case 'good':
        return 'bg-green-500/10 text-green-500 border-green-500/20';
      case 'low':
        return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
      case 'very-low':
        return 'bg-red-500/10 text-red-500 border-red-500/20';
      default:
        return 'bg-slate-500/10 text-slate-400 border-slate-500/20';
    }
  };

  const getConnectionIcon = () => {
    switch (connectionState) {
      case 'connected':
        return <Wifi className="w-4 h-4 text-green-500" />;
      case 'connecting':
      case 'reconnecting':
        return <Loader2 className="w-4 h-4 animate-spin text-yellow-500" />;
      case 'disconnected':
      case 'error':
        return <WifiOff className="w-4 h-4 text-red-500" />;
      default:
        return <Wifi className="w-4 h-4 text-slate-400" />;
    }
  };

  // Show reconnection banner
  if (connectionState === 'reconnecting' || connectionState === 'error') {
    return (
      <div className={`space-y-2 ${className}`}>
        <Alert className="bg-yellow-500/10 border-yellow-500/20">
          <AlertTriangle className="w-4 h-4 text-yellow-500" />
          <AlertDescription className="text-yellow-200">
            {connectionState === 'reconnecting' ? (
              <div className="flex items-center justify-between">
                <span>Reconnecting to the call... (Attempt {reconnectAttempts})</span>
                {showReconnectButton && reconnectAttempts >= 2 && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleManualReconnect}
                    disabled={isManualReconnecting}
                    className="ml-2"
                  >
                    {isManualReconnecting ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <RefreshCw className="w-3 h-3" />
                    )}
                    <span className="ml-1">Retry</span>
                  </Button>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <span>Connection error. Please try reconnecting.</span>
                {showReconnectButton && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleManualReconnect}
                    disabled={isManualReconnecting}
                    className="ml-2"
                  >
                    {isManualReconnecting ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <RefreshCw className="w-3 h-3" />
                    )}
                    <span className="ml-1">Reconnect</span>
                  </Button>
                )}
              </div>
            )}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Show quality indicator
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={`inline-flex items-center gap-2 ${className}`}>
            <Badge
              variant="outline"
              className={`flex items-center gap-1.5 px-2 py-1 ${getQualityColor()}`}
            >
              {getConnectionIcon()}
              {getQualityIcon()}
              {showLabel && (
                <span className="text-xs font-medium">{getQualityLabel()}</span>
              )}
            </Badge>
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="bg-slate-800 border-slate-700 text-white">
          <div className="text-sm">
            <p className="font-medium">Connection Quality: {getQualityLabel()}</p>
            {networkQuality === 'very-low' && (
              <p className="text-xs text-slate-400 mt-1">
                Consider turning off your video to improve audio quality
              </p>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default ConnectionQualityIndicator;










